import { View, StyleSheet, Keyboard, TouchableWithoutFeedback } from 'react-native';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Spacer from 'design-system/Spacer';
import Text, { styles } from 'design-system/Text';
import ToolTip from 'design-system/ToolTip';
import { IconBoldArrow } from 'design-system/icons';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import { KeyboardAvoidingButton } from 'design-system/KeyboardAvoidingButton';
import { AnimatedKeyboardAwareScrollView } from 'design-system/AnimatedComponents';
import { rem, AppView, CardView, withAnimatedNavigationHeader } from 'design-system';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { useMarginBottom } from 'hooks/useMarginBottom';
import usePasscodeModal from 'hooks/usePasscodeModal';
import createStyleSheets from 'utils/createStyleSheets';
import { MoneyTextInput } from 'utils/packages/moneyInput';
import { useAppSelector } from 'store/hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { parseMoneyInputText } from 'hooks/useMoneyInputText';
import { goBackToTopWithStops } from 'utils/navigationv6';
import { selectIsTradingAccountAlpaca, selectTradingAccount } from 'features/invest/reducers/selectors';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { getFractionDigits } from 'utils/formatting';
import { isIOS, isWeb } from '../../../constants';
import { SHARE_QUANTITY_DECIMALS } from '../constants';
import { useStockMeta, usePosition } from '../hooks';
import useOrderPreview from '../hooks/useOrderPreview';
import useOrderConfirm from '../hooks/useOrderConfirm';
import { formatAmount, formatNumberOfShares, isValidQuantityStep, roundQuantity } from '../utils';
import { strings as amountPreviewStrings } from '../components/AmountPreview';
import CashRow from '../components/CashRow';
import SellAssetRow from '../components/SellAssetRow';
const strings = {
    title: 'Sell',
    viewOrder: 'View order',
    belowMinError: 'Enter the number of shares to sell',
    exceedAvailableError: 'You don’t have that many shares available',
};
const SellAssetFromTradingAccountScreen = withAnimatedNavigationHeader(({ route, navigation, scrollHandler }) => {
    const textInputRef = useRef(null);
    const { assetId } = route.params;
    const stockCache = useStockMeta(assetId);
    const [position] = usePosition(assetId);
    const tradingAccount = useAppSelector(selectTradingAccount);
    const isAlpacaAccount = useAppSelector(selectIsTradingAccountAlpaca);
    const assetType = useAppSelector((store) => store.invest.caches[assetId]?.type);
    const minimumTransferUnit = useAppSelector((store) => store.invest.caches[assetId]?.minimumTransferUnit);
    const min = assetType === 'ETF' ? 0.0001 : minimumTransferUnit;
    const maximumFractionDigits = min !== undefined ? getFractionDigits(min) : SHARE_QUANTITY_DECIMALS;
    const [quantityText, setQuantityText] = useState('');
    const quantity = useMemo(() => parseMoneyInputText(quantityText), [quantityText]);
    const [amountError, setAmountError] = useState();
    const [fullOrderPreview, getOrderPreview, loading] = useOrderPreview(assetId, tradingAccount?.account.id, {
        operation: 'sell',
        updateOnMount: true,
        initialAmount: 0,
    });
    const [confirm, execute] = useOrderConfirm(stockCache?.symbol, stockCache?.name ?? '', assetId, tradingAccount?.account.id, !!isAlpacaAccount, tradingAccount?.account.accountType || 'GIA', 'sell');
    const [getPin] = usePasscodeModal();
    const colors = useColors();
    const styles = useStyles(styleSet);
    useLayoutEffect(() => {
        navigation.setOptions({
            title: strings.title,
            headerTintColor: colors.elements.primary,
            headerRight: () => <HelpScreenButton />,
        });
    }, [colors.elements.primary, navigation]);
    useEffect(() => {
        if (!tradingAccount?.account.id) {
            navigation.popToTop();
        }
    }, [assetId, tradingAccount?.account.id]);
    const checkAmountError = useCallback((value) => {
        if (value <= 0 || !isValidQuantityStep(value, min, maximumFractionDigits)) {
            setAmountError('belowMin');
        }
        else if (position && roundQuantity(value) > roundQuantity(position.availableToSell)) {
            setAmountError('exceedAvailable');
        }
        else {
            setAmountError(undefined);
        }
    }, [maximumFractionDigits, min, position]);
    useEffect(() => {
        if (typeof quantity === 'number') {
            checkAmountError(quantity);
        }
        else {
            setAmountError(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkAmountError]); // we want this hook to run only if checkAmountError updates
    useEffect(() => {
        if (!amountError) {
            getOrderPreview(quantity || 0);
        }
    }, [getOrderPreview, quantity, amountError]);
    const footnote = useMemo(() => {
        if (amountError === 'belowMin') {
            return { isError: true, text: strings.belowMinError };
        }
        if (amountError === 'exceedAvailable') {
            return { isError: true, text: strings.exceedAvailableError };
        }
        return {
            isError: false,
            text: (<>
              Value (est.){' '}
              <Text NumbersThin-14 Secondary>
                {formatAmount(fullOrderPreview?.order?.estimated.amount)}
              </Text>
            </>),
        };
    }, [amountError, fullOrderPreview?.order?.estimated.amount]);
    const onQuantityChange = useCallback((_formatted, extracted = '') => {
        setQuantityText(extracted);
        const value = parseMoneyInputText(extracted);
        if (typeof value === 'number') {
            checkAmountError(value);
        }
        else {
            setAmountError(undefined);
        }
    }, [checkAmountError]);
    const onSuccess = useCallback((order, marketOpeningInfo) => {
        goBackToTopWithStops(navigation, ['InvestPortfolio']);
        navigation.navigate('InvestOrder', { order, marketOpeningInfo });
    }, [navigation]);
    const onConfirmOrder = useCallback(async (orderPreview) => {
        try {
            const pin = await getPin({
                title: 'Enter Your Pin Code',
                biometryDescription: `You are selling ${formatNumberOfShares(orderPreview.estimated.quantity)}`,
            });
            try {
                await execute(orderPreview, onSuccess, pin);
            }
            catch (e) {
                if ('message' in e && e.message === 'Refresh token expired') {
                    onConfirmOrder(orderPreview);
                }
            }
        }
        catch {
            // user cancelled
        }
    }, [execute, getPin, onSuccess]);
    const handleContinue = useCallback(() => {
        Keyboard.dismiss();
        if (tradingAccount && quantity) {
            confirm({ operation: 'sell', quantity }, fullOrderPreview, onConfirmOrder);
        }
    }, [confirm, fullOrderPreview, onConfirmOrder, quantity, tradingAccount]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle, styles.contentContainer);
    if (!tradingAccount?.account.id)
        return null;
    return (<AppView withFrame={false}>
          <AnimatedKeyboardAwareScrollView enableOnAndroid onScroll={scrollHandler} scrollEventThrottle={16} keyboardShouldPersistTaps="handled" contentContainerStyle={contentContainerStyle} showsVerticalScrollIndicator={false}>
            <SellAssetRow assetId={assetId}/>
            <Spacer h={16}/>
            <View>
              <CashRow tradingAccountId={tradingAccount?.account.id}/>
              <View style={styles.iconContainer} pointerEvents="none">
                <View style={styles.iconWrapper}>
                  <IconBoldArrow alt colorKey="brand" direction="bottom"/>
                </View>
              </View>
            </View>
            <CardView noMarginBottom>
              <TouchableWithoutFeedback onPress={() => textInputRef.current?.focus()}>
                <View style={styles.amountContainer}>
                  <MoneyTextInput maximumIntegerDigits={9} placeholder="0" value={quantityText} ref={textInputRef} style={styles.amountInput} onChangeText={onQuantityChange} selectionColor={colors.text.brand} maximumFractionalDigits={maximumFractionDigits}/>
                  <View style={styles.footnoteRow}>
                    <Text TextThin-14 centered {...(footnote.isError ? { Red: true } : { Secondary: true })}>
                      {footnote.text}
                    </Text>
                    {!footnote.isError && (<>
                        <Spacer w={4}/>
                        <ToolTip small colorKey="secondary" title={amountPreviewStrings.tooltipTitle} subtitle={amountPreviewStrings.tooltipTextSeccl}/>
                      </>)}
                  </View>
                </View>
              </TouchableWithoutFeedback>
            </CardView>
          </AnimatedKeyboardAwareScrollView>
          <KeyboardAvoidingButton brand isFetching={loading} onPress={handleContinue} title={strings.viewOrder} disabled={!tradingAccount || !position || !quantity || !!amountError || !fullOrderPreview?.order}/>
        </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
    title: strings.title,
}));
const styleSet = createStyleSheets((colors) => ({
    iconContainer: {
        ...StyleSheet.absoluteFillObject,
        alignItems: 'center',
        top: -rem(30),
    },
    iconWrapper: {
        width: rem(42),
        height: rem(42),
        borderWidth: rem(1),
        alignItems: 'center',
        borderRadius: rem(44),
        justifyContent: 'center',
        borderColor: colors.borders.logoStroke,
        backgroundColor: colors.background.light,
    },
    amountContainer: {
        padding: rem(16),
        justifyContent: 'center',
    },
    amountInput: {
        textAlign: 'center',
        ...styles.Numbers_24,
        color: colors.text.primary,
        ...(isIOS && {
            alignSelf: 'center',
            textAlign: 'right',
        }),
    },
    footnoteRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
    },
    contentContainer: {
        paddingTop: rem(16),
    },
}));
export default SellAssetFromTradingAccountScreen;
